import { pdf } from "@react-pdf/renderer";
import { httpClient } from "infra/axios/axios-api";
import { DigitalSignApprover } from "domain/entities/digital-sign-approver";
import { Timesheet } from "domain/entities/timesheet";
import { Tripsheet } from "domain/entities/tripsheet";
import { Expense } from "domain/entities/expense";

import { DigitalSignStepEnum } from "domain/enum/digitalSignSteps";
import { MessageTypeEnum } from "domain/enum/messageType";
import { TaskTypeEnum } from "domain/enum/taskType";
import { TimesheetStatusEnum } from "domain/enum/timesheetStatus";
import { TripsheetStatusEnum } from "domain/enum/tripsheetStatus";
import { DigitalSignInputFilter } from "domain/models/digital-sign-input-filter";
import Spinner from "presentation/components/Shared/Spinner";
import TimesheetReportPDF from "presentation/components/Timesheet/TimesheetReportPDF";
import TripsheetReportPDF from "presentation/components/Tripsheet/TripsheetGrid/TripsheetMonthGrid/TripsheetReportPDF";
import { VacationSetting } from "presentation/components/Vacations/VacationSetting";
import ApprovalForm from "presentation/components/Expense/ExpenseGrid/ApprovalForm";
import {
  useDigitalSignByFilter,
  useUpdateDigitalSignMutation,
} from "presentation/hooks/use-query-digital-sign";
import {
  useCreateDigitalSignApproverMutation,
  useDigitalSignApproverByFilter,
} from "presentation/hooks/use-query-digital-sign-approver";
import { useEmployeesByFilter } from "presentation/hooks/use-query-employees";
import {
  useTimesheetsById,
  useUpdateTimesheetMutation,
} from "presentation/hooks/use-query-timesheet";
import { useTimesheetReportsByEmployee } from "presentation/hooks/use-query-timesheet-report";
import {
  useTripsheetById,
  useUpdateTripsheetMutation,
} from "presentation/hooks/use-query-tripsheet";
import { useTripsheetReport } from "presentation/hooks/use-query-tripsheet-report";
import { useExpenseById } from "presentation/hooks/use-query-expense";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";





interface Task {
  id: string;
  employeeId: string;
  type: string;
  description: string;
  dateRef?: Date;
  status: string;
}

interface ParamProps {
  // url: string;
  document: Task | undefined;
  onClose(): void;
}

const TaskApprovalForm: React.FC<ParamProps> = (params: ParamProps) => {
  const toast = useRef<Toast>(null);

  const { userEmployee, email: userEmployeeEmail } = useAuthStore();
  const [currentTimesheet, setCurrentTimesheet] = useState<Timesheet>();
  const [currentTripsheet, setCurrentTripsheet] = useState<Tripsheet>();

  const [url, setUrl] = useState("");

  const isTimesheet = useMemo(
    () => params.document?.type === TaskTypeEnum.TIMESHEET,
    [params.document?.type]
  );

  const isTripsheet = useMemo(
    () => params.document?.type === TaskTypeEnum.TRIPSHEET,
    [params.document?.type]
  );

  const isExpense = useMemo(
    () => params.document?.type === TaskTypeEnum.EXPENSE,
    [params.document?.type]
  );



  const { data: timesheetData } = useTimesheetsById(
    isTimesheet ? params.document?.id : undefined
  );
  const { data: tripsheetData } = useTripsheetById(
    isTripsheet ? params.document?.id : undefined
  );
  const { data: expenseData } = useExpenseById(
    isExpense ? params.document?.id : undefined
  );

  const { data: timesheetReportData, refetch: refetchReportTimesheetData } =
    useTimesheetReportsByEmployee(
      isTimesheet ? params.document?.id : undefined,
      {
        sortBy: "id",
        sortOrder: "asc",
        page: 1,
        limit: 100,
      }
    );

  const { data: tripsheetReportData, refetch: refetchReportTripsheetData } =
    useTripsheetReport(isTripsheet ? params.document?.id : undefined, {
      sortBy: "dateTrip",
      page: 1,
      limit: 100,
    });

  const { mutate: updateTimesheet, isSuccess: isSuccessTimesheet } =
    useUpdateTimesheetMutation();
  const { mutate: updateTripsheet } = useUpdateTripsheetMutation();

  const { data: digitalSignData, refetch: refetchDigitalSign } =
    useDigitalSignByFilter({
      referenceId: isTripsheet ? params.document?.id : undefined,
    } as DigitalSignInputFilter);

  const { data: digitalSignApprovalData, refetch: refetchDigitalSignApproval } =
    useDigitalSignApproverByFilter({
      referenceId: isTripsheet ? params.document?.id : undefined,
    });

  const {
    mutate: createDigitalSingApprover,
    isSuccess: isSuccessCreateApproval,
    isError: isErrorCreateApproval,
    error: errorCreateApproval,
  } = useCreateDigitalSignApproverMutation();

  const {
    isSuccess: isSuccessUpdateDigitalSign,
    isError: isErrorUpdateDigitalSign,
    error: errorUpdateDigitalSign,
  } = useUpdateDigitalSignMutation();

  const { data: employeeData } = useEmployeesByFilter(
    { email: timesheetReportData?.data?.email ?? "" },
    isTimesheet && !!timesheetReportData?.data?.email
  );

  useEffect(() => {
    if (isTimesheet) {
      setCurrentTimesheet(timesheetData?.data);
      refetchReportTimesheetData();
    } else if (isTripsheet) {
      setCurrentTripsheet(tripsheetData?.data);
      refetchReportTripsheetData();
    }
  }, [
    params.document,
    isTimesheet,
    isTripsheet,
    timesheetData?.data,
    tripsheetData?.data,
    refetchReportTimesheetData,
    refetchReportTripsheetData,
  ]);

  useEffect(() => {
    if (digitalSignData?.data) {
      refetchDigitalSignApproval();
    }
  }, [digitalSignData?.data, refetchDigitalSignApproval]);

  useEffect(() => {
    const generatePdf = async () => {
      if (!timesheetReportData?.data) {
        showMessage(MessageTypeEnum.ERROR, "Relatório", "Dados não encontrado");
        return;
      }

      try {
        const blob = await pdf(
          <TimesheetReportPDF data={timesheetReportData.data} isSuportService={employeeData?.data?.[0]?.isSuportService ?? false} />
        ).toBlob();
        const url = URL.createObjectURL(blob);
        setUrl(url);
      } catch (error) {
        showMessage(
          MessageTypeEnum.ERROR,
          "Relatório",
          "Falha ao gerar o ficheiro"
        );
      }
    };

    if (timesheetReportData?.data) {
      generatePdf();
    } else if (params.document?.type.includes("Mapa de Horas")) {
      setUrl("");
      showMessage(
        MessageTypeEnum.ERROR,
        "Relatório",
        "Não há dados para este relatório. Contactar o colaborador."
      );
      setTimeout(() => {
        params.onClose();
      }, 1500); // Reduzido de 3s para 1.5s
    }
  }, [timesheetReportData, employeeData, params]);

  useEffect(() => {
    const generatePdf = async () => {
      if (
        digitalSignApprovalData?.data &&
        params?.document?.status === TripsheetStatusEnum.ASSINADO_1
      ) {
        const digitalSign = digitalSignApprovalData?.data?.filter(
          (item) => item.digitalSign.referenceId === params.document?.id
        );

        const documentApprovedEmployee = digitalSign?.find(
          (item) => item.step === DigitalSignStepEnum.STEP_EMPLOYEE
        );
        let s3Path: string | undefined = "";
        if (documentApprovedEmployee?.documentPath) {
          s3Path = documentApprovedEmployee?.documentPath;
        } else {
          const documentApprovedNimber = digitalSign?.find(
            (item) => item.step === DigitalSignStepEnum.STEP_COMPANY
          );
          s3Path = documentApprovedNimber?.documentPath;
        }

        if (s3Path) {
          const signedUrlResponse = await httpClient.get(
            `/assets/${s3Path}`
          );

          const signedUrl = signedUrlResponse.data.url;
          if (signedUrl) {
            setUrl(signedUrl);
          } else {
            showMessage(
              MessageTypeEnum.ERROR,
              "Relatório",
              "Não foi possível obter URL do relatório."
            );
          }
        }
      }
    };
    if (digitalSignApprovalData?.data) {
      generatePdf();
    } else if (params.document?.type.includes("Mapa de KMs")) {
      setUrl("");
      showMessage(
        MessageTypeEnum.ERROR,
        "Relatório",
        "Não há dados para este relatório."
      );
      setTimeout(() => {
        params.onClose();
      }, 1500); // Reduzido de 3s para 1.5s
    }
  }, [digitalSignApprovalData, params]);

  useEffect(() => {
    const generatePdf = async () => {
      if (!tripsheetReportData?.data) {
        showMessage(MessageTypeEnum.ERROR, "Relatório", "Dados não encontrado");
        return;
      }

      try {
        if (
          tripsheetReportData?.data &&
          params.document?.status === TripsheetStatusEnum.A_ASSINAR
        ) {
          const blob = await pdf(
            <TripsheetReportPDF data={tripsheetReportData?.data} />
          ).toBlob();
          const url = URL.createObjectURL(blob);
          setUrl(url);
        }
      } catch (error) {
        showMessage(
          MessageTypeEnum.ERROR,
          "Relatório",
          "Falha ao gerar o ficheiro"
        );
      }
    };

    if (tripsheetReportData?.data) {
      generatePdf();
    } else if (params.document?.type.includes("Mapa de KMs")) {
      setUrl("");
      showMessage(
        MessageTypeEnum.ERROR,
        "Relatório",
        "Não há dados para este relatório."
      );
      setTimeout(() => {
        params.onClose();
      }, 1500); // Reduzido de 3s para 1.5s
    }
  }, [tripsheetReportData, params]);

  useEffect(() => {
    if (isSuccessTimesheet && currentTimesheet) {
      if (currentTimesheet.status === TimesheetStatusEnum.APROVADO) {
        showMessage(
          MessageTypeEnum.SUCCESS,
          "Workflow",
          "Documento foi aprovado com sucesso"
        );
      } else if (currentTimesheet.status === TimesheetStatusEnum.EDITADO) {
        showMessage(
          MessageTypeEnum.WARN,
          "Workflow",
          "Documento foi reprovado e retornou ao estado de edição"
        );
      }
      setTimeout(() => {
        params.onClose();
      }, 1500);
    }
  }, [isSuccessTimesheet, currentTimesheet, params]);

  useEffect(() => {
    if (isErrorUpdateDigitalSign) {
      console.error(errorUpdateDigitalSign?.response.data.metadata.errors[0]);
      showMessage(
        MessageTypeEnum.ERROR,
        "Workflow",
        "Erro ao atualizar a assinatura digital"
      );
    }
  }, [isErrorUpdateDigitalSign, errorUpdateDigitalSign]);

  useEffect(() => {
    if (isErrorCreateApproval) {
      console.log(
        "errorCreateApproval",
        errorCreateApproval?.response.data.metadata.errors[0]
      );
      showMessage(
        MessageTypeEnum.ERROR,
        "Workflow",
        "Erro ao assinar documento digital"
      );
    }
  }, [isErrorCreateApproval, errorCreateApproval]);

  useEffect(() => {
    if (currentTripsheet?.id) {
      refetchDigitalSign();
    }
  }, [currentTripsheet?.id, refetchDigitalSign]);

  useEffect(() => {
    if (
      isSuccessCreateApproval &&
      currentTripsheet &&
      currentTripsheet.status !== TripsheetStatusEnum.A_ASSINAR
    ) {
      updateTripsheet(currentTripsheet);
      if (
        currentTripsheet.status === TripsheetStatusEnum.ASSINADO_2 ||
        currentTripsheet.status === TripsheetStatusEnum.ASSINADO_1
      ) {
        showMessage(
          MessageTypeEnum.SUCCESS,
          "Workflow",
          "Documento foi aprovado com sucesso"
        );
      }
      setTimeout(() => {
        params.onClose();
      }, 1500);
    }
  }, [isSuccessCreateApproval, currentTripsheet, params, updateTripsheet]);

  useEffect(() => {
    if (
      isSuccessUpdateDigitalSign &&
      currentTripsheet?.status === TripsheetStatusEnum.REJEITADO
    ) {
      // updateTripsheet(currentTripsheet);
      showMessage(
        MessageTypeEnum.ERROR,
        "Workflow",
        "Documento foi reprovado e deverá ser atualizado pela equipa de Finanças"
      );
      setTimeout(() => {
        params.onClose();
      }, 1500);
    }
  }, [isSuccessUpdateDigitalSign, currentTripsheet, params]);

  const showMessage = (
    type: MessageTypeEnum,
    subject: string,
    message: string
  ) => {
    toast.current?.show({
      severity: type,
      summary: subject,
      detail: message,
      life: 1500,
    });
  };

  const handleApprove = async () => {
    if (params.document?.id && params.document.type) {
      switch (params.document.type) {
        case TaskTypeEnum.TIMESHEET:
          const timesheet = timesheetData?.data;
          if (
            timesheet &&
            timesheet.status === TimesheetStatusEnum.ENVIADO &&
            userEmployeeEmail
          ) {
            timesheet.status = TimesheetStatusEnum.APROVADO;

            timesheet.lastModifiedBy = userEmployeeEmail;
            setCurrentTimesheet({ ...timesheet });
            updateTimesheet(timesheet);
          }

          break;
        case TaskTypeEnum.TRIPSHEET:
          let tripsheet = tripsheetData?.data;
          const digitalSign = digitalSignData?.data?.find(
            (item) => item.referenceId === tripsheet?.id
          );
          if (
            tripsheet &&
            tripsheet.status === TripsheetStatusEnum.A_ASSINAR &&
            userEmployeeEmail &&
            userEmployee?.id &&
            digitalSign
          ) {
            tripsheet.status = TripsheetStatusEnum.ASSINADO_1;

            tripsheet.lastModifiedBy = userEmployeeEmail;
            setCurrentTripsheet({ ...tripsheet });

            const signApproval: DigitalSignApprover = {
              employee: {
                id: userEmployee?.id,
              },
              digitalSign: digitalSign,
              step: DigitalSignStepEnum.STEP_COMPANY,
            };
            createDigitalSingApprover(signApproval);
          } else if (
            tripsheet &&
            tripsheet.status === TripsheetStatusEnum.ASSINADO_1 &&
            userEmployeeEmail &&
            userEmployee?.id &&
            digitalSign
          ) {
            tripsheet.status = TripsheetStatusEnum.ASSINADO_2;

            tripsheet.lastModifiedBy = userEmployeeEmail;
            setCurrentTripsheet({ ...tripsheet });

            const signApproval: DigitalSignApprover = {
              employee: {
                id: userEmployee?.id,
              },
              digitalSign: digitalSign,
              step: DigitalSignStepEnum.STEP_EMPLOYEE,
            };
            createDigitalSingApprover(signApproval);
          }

          break;
        case TaskTypeEnum.VACATION_PLAN:
          break;
      }
    }
  };

  // const handleReject = async () => {
  //   if (params.document?.id && params.document.type) {
  //     switch (params.document.type) {
  //       case TaskTypeEnum.TIMESHEET:
  //         const timesheet = timesheetData?.data;
  //         if (
  //           timesheet &&
  //           timesheet.status === TimesheetStatusEnum.ENVIADO &&
  //           userEmployeeEmail
  //         ) {
  //           timesheet.status = TimesheetStatusEnum.EDITADO;
  //           timesheet.lastModifiedBy = userEmployeeEmail;
  //           setCurrentTimesheet({ ...timesheet });
  //           updateTimesheet(timesheet);
  //         }

  //         break;
  //       case TaskTypeEnum.TRIPSHEET:
  //         let tripsheet = tripsheetData?.data;
  //         let digitalSign = digitalSignData?.data?.find(
  //           (item) => item.referenceId === tripsheet?.id
  //         );
  //         if (
  //           tripsheet &&
  //           (tripsheet.status === TripsheetStatusEnum.A_ASSINAR ||
  //             tripsheet.status === TripsheetStatusEnum.ASSINADO_1) &&
  //           userEmployeeEmail &&
  //           userEmployee?.id &&
  //           digitalSign
  //         ) {
  //           tripsheet.status = TripsheetStatusEnum.REJEITADO;
  //           tripsheet.lastModifiedBy = userEmployeeEmail;
  //           setCurrentTripsheet({ ...tripsheet });

  //           digitalSign.status = DigitalSignStatusEnum.CANCELADO;
  //           updateDigitalSign(digitalSign).then(() => {
  //             if (tripsheet) updateTripsheet(tripsheet);
  //           });
  //         }

  //         break;
  //       case TaskTypeEnum.VACATION_PLAN:
  //         break;
  //     }
  //   }
  // };

  if (
    !tripsheetReportData?.data &&
    !timesheetReportData?.data &&
    !params.document?.type.includes("Férias") &&
    !params.document?.type.includes("Despesa")
  ) {
    return (
      <div>
        <Spinner />
        <Toast ref={toast} position="bottom-right" />
      </div>
    );
  }

  return (
    <div>
      {" "}
      {!params.document?.type.includes("Férias") && !params.document?.type.includes("Despesa") && (
        <div>
          <iframe
            src={`${url}#navpanes=0`}
            width="100%"
            height="500px"
            title="Timesheet Report"
            style={{ border: "none" }}
          ></iframe>
          <div style={{ textAlign: "right", marginTop: "20px" }}>
            {/* <Button
              label="Rejeitar"
              className="p-button-danger"
              icon="pi pi-times"
              onClick={(e) => {
                console.log(e);
                e.stopPropagation();
                handleReject();
              }}
            />{" "} */}
            <Button
              label="Assinar"
              icon="pi pi-check"
              onClick={(e) => {
                e.stopPropagation();
                handleApprove();
              }}
            />
          </div>
        </div>
      )}
      {params.document?.type.includes("Pedido de Férias") && (
        <VacationSetting
          managerId={userEmployee?.id}
          isAdmin={false}
          isManager={true}
          date={params.document.dateRef || new Date()}
          vacationId={params.document?.id}
          onClose={() => {
            params.onClose();
          }}
          task="Pedido"
        />
      )}
      {params.document?.type.includes("Pedido de Cancelamento de Férias") && (
        <VacationSetting
          managerId={userEmployee?.id}
          isAdmin={false}
          isManager={true}
          date={params.document.dateRef || new Date()}
          vacationId={params.document?.id}
          onClose={() => {
            params.onClose();
          }}
          task="Cancelamento"
        />
      )}
      {params.document?.type.includes("Despesa") && expenseData?.data && (
        <ApprovalForm
          expense={expenseData.data}
          onClose={() => {
            params.onClose();
          }}
          showMessage={(type, subject, message) => {
            toast.current?.show({
              severity: type,
              summary: subject,
              detail: message,
              life: 1500,
            });
          }}
        />
      )}
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};

export default TaskApprovalForm;
